/**
 * PracticeAnswerGrid - Shared 2x2 answer grid component for practice screens
 *
 * Used across all PreK practice screens to display answer choices
 */

import React from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';

interface PracticeAnswerGridProps {
  choices: number[];
  correctAnswer: number;
  hasAnswered: boolean;
  isTTSPlaying: boolean;
  highlightCorrect?: boolean;
  wrongAnswer?: number | null;
  waitingForCorrectAnswer?: boolean;
  onChoiceClick: (choice: number) => void;
}

const PracticeAnswerGrid: React.FC<PracticeAnswerGridProps> = ({
  choices,
  correctAnswer,
  hasAnswered,
  isTTSPlaying,
  highlightCorrect = false,
  wrongAnswer = null,
  waitingForCorrectAnswer = false,
  onChoiceClick,
}) => {
  const isDisabled = hasAnswered || isTTSPlaying;

  const getButtonClasses = (choice: number): string => {
    const baseClasses =
      'h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300';

    // Wrong answer flash (red pulse)
    if (wrongAnswer === choice) {
      return `${baseClasses} bg-red-500 text-white border-4 border-red-700 shadow-red-300 animate-pulse`;
    }

    // Highlight correct answer (We-Do phase)
    if (highlightCorrect && choice === correctAnswer) {
      return `${baseClasses} bg-green-500 text-white border-4 border-green-700 shadow-green-300 animate-pulse`;
    }

    // Correct answer (after answering)
    if (hasAnswered && choice === correctAnswer) {
      return `${baseClasses} bg-green-500 text-white border-4 border-green-700 shadow-green-300`;
    }

    // Waiting for correct answer - highlight correct, gray others
    if (waitingForCorrectAnswer && choice === correctAnswer) {
      return `${baseClasses} bg-green-500 text-white border-4 border-green-700 shadow-green-300 animate-pulse`;
    }

    if (waitingForCorrectAnswer && choice !== correctAnswer) {
      return `${baseClasses} bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed`;
    }

    // Disabled state (answered or TTS playing)
    if (isDisabled) {
      return `${baseClasses} bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed`;
    }

    // Default interactive state
    return `${baseClasses} bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu`;
  };

  const renderChoiceContent = (choice: number) => {
    return <span>{choice}</span>;
  };

  return (
    <div className="grid grid-cols-2 gap-8 lg:gap-12 w-full max-w-md lg:max-w-lg">
      {choices.map((choice, index) => (
        <motion.button
          key={choice}
          initial={{ opacity: 0, y: 30, rotate: -5 }}
          animate={{
            opacity: 1,
            y: 0,
            rotate: 0,
            scale: highlightCorrect && choice === correctAnswer ? [1, 1.1, 1] : 1,
            boxShadow:
              highlightCorrect && choice === correctAnswer
                ? [
                    '0 0 0 0 rgba(34, 197, 94, 0.7)',
                    '0 0 0 20px rgba(34, 197, 94, 0)',
                    '0 0 0 0 rgba(34, 197, 94, 0)',
                  ]
                : undefined,
          }}
          transition={{
            duration: 0.4,
            delay: index * 0.1,
            scale: {
              duration: highlightCorrect && choice === correctAnswer ? 2 : 0.4,
              repeat: highlightCorrect && choice === correctAnswer ? 1 : 0,
            },
          }}
          whileHover={!isDisabled ? { scale: 1.08, rotate: 1 } : {}}
          whileTap={!isDisabled ? { scale: 0.92 } : {}}
          onClick={() => onChoiceClick(choice)}
          disabled={isDisabled}
          className={getButtonClasses(choice)}
          style={NUMERAL_FONT_STYLE}
        >
          {renderChoiceContent(choice)}
        </motion.button>
      ))}
    </div>
  );
};

export default PracticeAnswerGrid;
